import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  TdsIcon,
  TdsSideMenu,
  TdsSideMenuCloseButton,
  TdsSideMenuItem,
} from '@scania/tegel-react'
import { closeMainMenuMobile, getMenuState } from '../../../store/menuSlice'
import { BreakpointWidthPx } from '../../../css/BreakpointWidthPx'
import { useCallback, useRef, useState } from 'react'
import {
  getGuidedViewPage,
  getIsLoggedInState,
  getSessionInitDataState,
  getSummaryOrRfqOpenedFromPage,
  setSummaryOrRfqOpenedFromPage,
} from '../../../store/sessionDataSlice'
import { GuidedViewPageType, SidePanelContent } from '../../../store/types'
import {
  openGvPanel,
  setGvSidePanelContent,
} from '../../../store/sidePanelSlice'
import { ActiveChoicesIcon } from '../ActiveChoicesIcon'
import { getLatestConfigChangeResult } from '../../../store/truckInfoSlice'
import { useActiveChoicesHighlight } from '../ActiveChoicesHighlight'
import { TextId, useTextsV2 } from '../../../utils/useTexts'
import { BreakpointHeightPx } from '../../../css/BreakpointHeightPx'
import {
  pushAdobeEvent,
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingSearchResultClick,
  ScaniaAdobeTrackingStartOverClick,
} from '../../../utils/adobeAnalytics'
import { SvgCross, SvgSearch } from '../../../components/SvgImports'
import { SESSION_FAILURE } from '../../../api/errors'
import { getMarketLanguageState } from '../../../store/appSlice'
import { ScaniaSearchResult } from '../../../api/generated'
import { useClient } from '../../../utils/useClient'
import {
  buildRelativeAllViewPage,
  buildRelativeGvPage,
  buildRelativeOverviewPage,
  buildRelativeSummaryPage,
} from '../../../utils/UrlBuilders'
import { getUrlParametersToString } from '../../../utils/getUrlParametersToString'
import { useNavigate } from 'react-router-dom'

const MainMenuRoot = styled.div`
  width: 100%;
  height: 100%;
  background-color: var(--tds-white);
  top: 0;
  left: 0;
  position: fixed;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  display: none;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: flex;
  }

  button {
    box-sizing: border-box;
  }
`

const SearchWrapper = styled.div`
  display: flex;
  padding-left: 24px;
  height: 68px;
  align-items: center;
`

const SearchField = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  font-family: 'Scania Sans Semi Condensed', arial, helvetica;

  &::placeholder {
    color: var(--tds-sidebar-side-menu-single-item-color);
  }
`

const SearchIconWrapper = styled.div`
  padding-right: 8px;

  svg {
    height: 24px;
    width: 24px;
  }
`

const SearchResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: var(--tds-grey-50);
`

const SearchResult = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
  padding-left: 16px;
  border-bottom: 1px solid var(--tds-grey-100);

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
      background-color: var(--tds-grey-100);
    }
  }
`

const NoSearchResult = styled.div`
  display: flex;
  font-style: italic;
  padding: 16px;
`

interface IconWrapperSearchCloseProps {
  $showClose: boolean
}

const IconWrapperSearchClose = styled.div<IconWrapperSearchCloseProps>`
  width: 68px;
  height: 100%;
  display: flex;
  align-items: center;
  visibility: ${(props) => (props.$showClose ? '' : 'hidden')};

  svg {
    height: 20px;
    width: 20px;
    margin-left: 24px;
    margin-right: 24px;
  }
`

interface MainMenuMobileProps {
  handleNewClick: () => void
  handleSessionFailure: () => void
  navigateToMenu: (
    id: string,
    menuPath: string,
    gvPageToJumpTo: GuidedViewPageType,
  ) => void
}

export function MainMenuMobileTds({
  handleNewClick,
  handleSessionFailure,
  navigateToMenu,
}: MainMenuMobileProps): JSX.Element {
  const dispatch = useAppDispatch()
  const client = useClient()
  const t2 = useTextsV2()
  const navigate = useNavigate()
  const mainMenuIsOpen = useAppSelector(getMenuState).mainMenuMobileIsOpen
  const refMainMenu = useRef<HTMLTdsSideMenuElement>(null)
  const isLoggedIn = useAppSelector(getIsLoggedInState)
  const guidedViewPage = useAppSelector(getGuidedViewPage)
  const latestConfigChangeResult = useAppSelector(getLatestConfigChangeResult)
  const activeChoicesState = latestConfigChangeResult?.activeChoices
  const highlightYourChoices = useActiveChoicesHighlight(
    activeChoicesState || [],
  )
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const summaryOrRfqOpenedFromPage = useAppSelector(
    getSummaryOrRfqOpenedFromPage,
  )
  const [searchString, setSearchString] = useState<string>('')
  const [searchResult, setSearchResult] = useState<ScaniaSearchResult[] | null>(
    null,
  )
  let searchLenMin = 2
  const updateSearchField = useRef<boolean>(false)

  const handleCloseMainMenu = () => {
    dispatch(closeMainMenuMobile())
  }

  const handleSaveClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(closeMainMenuMobile())
    dispatch(openGvPanel())
    dispatch(setGvSidePanelContent(SidePanelContent.save))
    if (
      guidedViewPage === GuidedViewPageType.summary ||
      guidedViewPage === GuidedViewPageType.rfq
    ) {
      handleGoBackClick()
    }
  }

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(closeMainMenuMobile())
    dispatch(openGvPanel())
    dispatch(setGvSidePanelContent(SidePanelContent.share))
    if (
      guidedViewPage === GuidedViewPageType.summary ||
      guidedViewPage === GuidedViewPageType.rfq
    ) {
      handleGoBackClick()
    }
  }

  const handleActiveChangesClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(closeMainMenuMobile())
    dispatch(openGvPanel())
    dispatch(setGvSidePanelContent(SidePanelContent.activeChanges))
    if (
      guidedViewPage === GuidedViewPageType.summary ||
      guidedViewPage === GuidedViewPageType.rfq
    ) {
      handleGoBackClick()
    }
  }

  const handleAccountClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isLoggedIn) {
      dispatch(openGvPanel())
      dispatch(setGvSidePanelContent(SidePanelContent.garage))
    } else {
      dispatch(openGvPanel())
      dispatch(setGvSidePanelContent(SidePanelContent.login))
    }
    dispatch(closeMainMenuMobile())
    if (
      guidedViewPage === GuidedViewPageType.summary ||
      guidedViewPage === GuidedViewPageType.rfq
    ) {
      handleGoBackClick()
    }
  }

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(openGvPanel())
    dispatch(setGvSidePanelContent(SidePanelContent.info))
    dispatch(closeMainMenuMobile())
    if (
      guidedViewPage === GuidedViewPageType.summary ||
      guidedViewPage === GuidedViewPageType.rfq
    ) {
      handleGoBackClick()
    }
  }

  const handleSummaryClick = (e: React.MouseEvent) => {
    dispatch(closeMainMenuMobile())
    if (!marketLanguage) {
      throw new Error('Expected market language.')
    }
    if (guidedViewPage !== GuidedViewPageType.rfq) {
      dispatch(setSummaryOrRfqOpenedFromPage(guidedViewPage))
    }
    const relativeUrl = buildRelativeSummaryPage(marketLanguage)
    const params = relativeUrl.params
    const newUrl = relativeUrl.path + getUrlParametersToString(params)
    navigate(newUrl)
  }

  const handleGoBackClick = () => {
    if (!marketLanguage) {
      throw new Error('Expected market language.')
    }
    if (guidedViewPage === GuidedViewPageType.guidedView) {
      const relativeUrl = buildRelativeOverviewPage(marketLanguage)
      const params = relativeUrl.params
      const newUrl = relativeUrl.path + getUrlParametersToString(params)
      navigate(newUrl)
    }
    if (guidedViewPage === GuidedViewPageType.allView) {
      const relativeUrl = buildRelativeGvPage(marketLanguage)
      const params = relativeUrl.params
      const newUrl = relativeUrl.path + getUrlParametersToString(params)
      navigate(newUrl)
    }
    if (
      guidedViewPage === GuidedViewPageType.summary ||
      guidedViewPage === GuidedViewPageType.rfq
    ) {
      var relativeUrl = buildRelativeGvPage(marketLanguage)
      if (summaryOrRfqOpenedFromPage === GuidedViewPageType.allView) {
        relativeUrl = buildRelativeAllViewPage(marketLanguage)
      }
      const params = relativeUrl.params
      const newUrl = relativeUrl.path + getUrlParametersToString(params)
      navigate(newUrl)
    }
    dispatch(closeMainMenuMobile())
  }

  const handleNewConfigClick = () => {
    handleNewClick()
    const trackingEvent: ScaniaAdobeTrackingStartOverClick = {
      event: ScaniaAdobeEventId.StartOverClick,
      eventInfo: { layout: 'desktop' },
    }
    pushAdobeEvent(trackingEvent)
  }

  const capitalizeFirstLetter = (text: string | null | undefined) => {
    if (!text) {
      return ''
    }
    const smallText = text.toLowerCase()
    const capitalizedText =
      smallText.charAt(0).toUpperCase() + smallText.slice(1)
    return capitalizedText || ''
  }

  const textSearchPlaceholde = capitalizeFirstLetter(t2(TextId.SEARCH))

  const handleSearch = useCallback(
    async (event: any) => {
      if (!sessionInitData) {
        throw new Error('Expected sessionInitData to be defined.')
      }
      if (!marketLanguage) {
        throw new Error('Expected marketLanguage to be defined.')
      }
      if (!client) {
        throw new Error('Expected client to be defined.')
      }
      const typedSearchString = event.target.value
      setSearchString(typedSearchString)
      if (typedSearchString && typedSearchString.length >= searchLenMin) {
        let searchResultTyped
        try {
          const response = await client.scaniaSearch(
            sessionInitData.sessionId,
            typedSearchString,
          )
          if (response.error === SESSION_FAILURE) {
            handleSessionFailure()
            return
          }
          // TODO: Investigate if null or undefined in the response is an error
          // or not.
          searchResultTyped = response.scaniaSearch || null
        } catch {
          console.log('Search went wrong.')
          return
        }
        setSearchResult(searchResultTyped || null)
      } else {
        setSearchResult(null)
      }
    },
    [
      client,
      marketLanguage,
      sessionInitData,
      searchLenMin,
      handleSessionFailure,
    ],
  )

  const handleSearchResultClick = (
    clickedResult: ScaniaSearchResult,
    resultCount: number,
  ) => {
    const trackingEvent: ScaniaAdobeTrackingSearchResultClick = {
      event: ScaniaAdobeEventId.SearchResultClick,
      eventInfo: {
        searchTerm: searchString,
        searchResultCount: resultCount,
        searchResultClickTitle: clickedResult.description,
      },
    }
    pushAdobeEvent(trackingEvent)
    navigateToMenu(
      clickedResult.id,
      clickedResult.path,
      GuidedViewPageType.allView,
    )
    setSearchString('')
    setSearchResult(null)
  }

  const handleSearchClose = () => {
    setSearchString('')
    setSearchResult(null)
    updateSearchField.current = true
  }

  return (
    <MainMenuRoot data-name="MainMenu">
      <TdsSideMenu
        aria-label="Side menu"
        ref={refMainMenu}
        class={mainMenuIsOpen ? 'menu-opened' : ''}
        open={mainMenuIsOpen}
      >
        <TdsSideMenuCloseButton onClick={handleCloseMainMenu} />
        {guidedViewPage === GuidedViewPageType.allView && (
          <TdsSideMenuItem>
            <SearchWrapper>
              <SearchIconWrapper>
                <SvgSearch />
              </SearchIconWrapper>
              <SearchField
                placeholder={textSearchPlaceholde}
                type="text"
                onChange={handleSearch}
                key={updateSearchField.current.toString()}
              />
              <IconWrapperSearchClose
                $showClose={searchString ? true : false}
                onClick={handleSearchClose}
              >
                <SvgCross />
              </IconWrapperSearchClose>
            </SearchWrapper>
          </TdsSideMenuItem>
        )}
        {searchResult && searchResult.length !== 0 && (
          <SearchResultContainer>
            {searchResult?.map((text) => {
              return (
                <SearchResult
                  key={text.id}
                  onClick={() =>
                    handleSearchResultClick(text, searchResult.length)
                  }
                >
                  {text.description}
                </SearchResult>
              )
            })}
          </SearchResultContainer>
        )}
        {searchResult && searchResult.length === 0 && (
          <NoSearchResult>{t2(TextId.SEARCH_NO_HITS)}</NoSearchResult>
        )}
        {searchString && searchString.length < searchLenMin && (
          <NoSearchResult>{t2(TextId.SEARCH_MIN_LENGTH)}</NoSearchResult>
        )}
        {!searchResult && !searchString && (
          <>
            {guidedViewPage !== GuidedViewPageType.overview && (
              <TdsSideMenuItem onClick={handleActiveChangesClick}>
                <button>
                  <TdsIcon size="24px">
                    <ActiveChoicesIcon
                      choiceCount={activeChoicesState?.length || 0}
                      isHighlighted={highlightYourChoices}
                    />
                  </TdsIcon>
                  {t2(TextId.ACTION_LABEL_YOUR_CHANGES)}
                </button>
              </TdsSideMenuItem>
            )}
            {guidedViewPage !== GuidedViewPageType.overview &&
              guidedViewPage !== GuidedViewPageType.summary && (
                <TdsSideMenuItem onClick={handleSummaryClick}>
                  <button>
                    <TdsIcon name="document" size="24px" />
                    {t2(TextId.LABEL_ACTION_SUMMARY)}
                  </button>
                </TdsSideMenuItem>
              )}
            {guidedViewPage !== GuidedViewPageType.overview && (
              <TdsSideMenuItem onClick={handleSaveClick}>
                <button>
                  <TdsIcon name="save" size="24px" />
                  {t2(TextId.LABEL_ACTION_SAVE)}
                </button>
              </TdsSideMenuItem>
            )}
            {guidedViewPage !== GuidedViewPageType.overview && (
              <TdsSideMenuItem onClick={handleShareClick}>
                <button>
                  <TdsIcon name="share" size="24px" />
                  {t2(TextId.LABEL_ACTION_SHARE)}
                </button>
              </TdsSideMenuItem>
            )}
            <TdsSideMenuItem onClick={handleAccountClick}>
              <button>
                <TdsIcon name="profile" size="24px" />
                {t2(TextId.ACTION_LABEL_MY_GARAGE)}
              </button>
            </TdsSideMenuItem>
            <TdsSideMenuItem onClick={handleInfoClick}>
              <button>
                <TdsIcon name="info" size="24px" />
                {t2(TextId.LABEL_ABOUT)}
              </button>
            </TdsSideMenuItem>
          </>
        )}

        {guidedViewPage === GuidedViewPageType.guidedView && (
          <TdsSideMenuItem slot="end" onClick={handleGoBackClick}>
            <button>
              <TdsIcon name="back" size="24px" />
              {t2(TextId.ACTION_LABEL_OVERVIEW)}
            </button>
          </TdsSideMenuItem>
        )}
        {guidedViewPage === GuidedViewPageType.allView && (
          <TdsSideMenuItem slot="end" onClick={handleGoBackClick}>
            <button>
              <TdsIcon name="back" size="24px" />
              {t2(TextId.ACTION_LABEL_VIEW_LESS)}
            </button>
          </TdsSideMenuItem>
        )}
        {(guidedViewPage === GuidedViewPageType.summary ||
          guidedViewPage === GuidedViewPageType.rfq) && (
          <TdsSideMenuItem slot="end" onClick={handleGoBackClick}>
            <button>
              <TdsIcon name="back" size="24px" />
              {t2(TextId.ACTION_LABEL_BACK_CONFIGURATION)}
            </button>
          </TdsSideMenuItem>
        )}
        <TdsSideMenuItem slot="end" onClick={handleNewConfigClick}>
          <button>
            <TdsIcon name="truck" size="24px" />
            {t2(TextId.ACTION_LABEL_NEW_CONFIGURATION)}
          </button>
        </TdsSideMenuItem>
      </TdsSideMenu>
    </MainMenuRoot>
  )
}
